import { getPushlySdk } from '../../_utils/pushly-sdk'
import { isIOSStandalone } from './ios-detection'

/**
 * Attempts to recover a lapsed push subscription on iOS PWA.
 *
 * Safari deletes the service worker registration after 7 days of inactivity.
 * When the user returns, the push subscription is gone but the notification
 * permission grant is still intact. This function detects that state and
 * triggers the SDK to re-subscribe silently (no native prompt shown if
 * permission was previously granted).
 *
 * Should be called once on mobile root mount (standalone mode only).
 * No-ops on non-iOS, non-standalone, or when the subscription is healthy.
 */
export async function recoverPushSubscriptionIfNeeded(): Promise<void> {
    // Only relevant for iOS in standalone (PWA) mode
    if (!isIOSStandalone()) return

    // Service worker API required
    if (!navigator.serviceWorker) return

    // Check if notification permission was previously granted
    if (Notification.permission !== 'granted') return

    // Check if the service worker registration still exists
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
        // SW exists — check if push subscription is still active
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) return // Subscription healthy, nothing to do
    }

    // SW or subscription is gone (Safari 7-day expiry) but permission is still granted.
    // Trigger the SDK to re-register. Since permission is already granted,
    // the native prompt won't appear — the SDK will silently re-subscribe.
    const sdk = getPushlySdk()
    if (!sdk) return

    console.debug('[PushSubscriptionRecovery] Subscription lapsed, triggering re-registration')
    sdk.PushNotifications.showPermissionPrompt({ skipEligibilityCheck: true })
}
